import angular from "angular";
import {
  IExpressionFunction,
  ITemplateOptions,
  IValidator,
  IWatcher,
} from "AngularFormly";
import { Record } from "../../../../server/crud";
import { IQuery } from "../../../../server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../server/serverUtility";

interface DefaultItem extends Record {
  Name: string;
}

export interface RecordSelectAsyncTemplateOptions extends ITemplateOptions {
  queryUrl: string;
  queryBuilder?: QueryBuilder;
  getDefaultItem: (model: any) => DefaultItem | undefined;
  serverItemMapper?: (model: any) => DefaultItem;
  useWrappedQuery?: boolean;
  model?: string | object;
  expressionProperties?: {
    [key: string]: string | IExpressionFunction | IValidator;
  };
  hideExpression?: string | IExpressionFunction;
  watcher?: IWatcher | IWatcher[] | undefined;
  disableDefaultQueryCaching?: boolean;
  onSelect?: (selectedItem: unknown, model: unknown) => void;
  className?: string;
  helpText?: (model: any) => string;
}

export interface QueryBuilder {
  (search: string, model: Record): IQuery;
}

interface RecordSelectAsyncControllerScope extends angular.IScope {
  to: RecordSelectAsyncTemplateOptions;
  model: Record;
  currentItems: Record[];
  valueProp: string;
  labelProp: string;
  formState: any;
  onSelect: (selected: any) => void;
  query: (search: string, model: Record) => void;
  selectedItem: {} | undefined;
}

export class RecordSelectAsyncController {
  static $inject = ["$scope", ServerUtilityName];
  defaultQueryPromise: angular.IPromise<Record[]> | undefined;
  private readonly queryBuilder: QueryBuilder;

  get templateOptions(): RecordSelectAsyncTemplateOptions {
    return this.$scope.to;
  }

  constructor(
    private readonly $scope: RecordSelectAsyncControllerScope,
    private readonly serverUtility: ServerUtility
  ) {
    $scope.currentItems = [];
    $scope.valueProp ??= "Id";
    $scope.labelProp ??= "Name";
    $scope.onSelect = (selected) => this.onSelect(selected);
    $scope.query = (search) => this.query(search);
    this.queryBuilder =
      this.templateOptions.queryBuilder ??
      RecordSelectAsyncController.defaultQueryBuilder;
    //this isn't called by angular for some reason
    this.$onInit();
  }
  $onInit() {
    const model = this.$scope.model;
    const initialItem = this.$scope.model
      ? this.templateOptions.getDefaultItem(model)
      : undefined;
    this.$scope.selectedItem = initialItem;
    const disableDefaultQueryCaching =
      this.templateOptions.disableDefaultQueryCaching ?? false;
    if (!disableDefaultQueryCaching) {
      this.defaultQueryPromise = this.getResultsFromServer(
        this.queryBuilder("", model)
      );
    }

    if (initialItem === undefined) {
      return;
    }
    this.$scope.currentItems = [initialItem];
  }

  onSelect(selected: any) {
    this.$scope.selectedItem = selected;
    this.templateOptions.onSelect?.call(this, selected, this.$scope.model);
  }

  query(search: string) {
    if (
      !search &&
      !(this.templateOptions.disableDefaultQueryCaching ?? false)
    ) {
      this.defaultQueryPromise?.then((r) => (this.$scope.currentItems = r));
      return;
    }
    const query = this.queryBuilder(search, this.$scope.model);
    this.getResultsFromServer(query).then((results) => {
      this.$scope.currentItems = results.map((r) =>
        (
          this.templateOptions.serverItemMapper ??
          RecordSelectAsyncController.DefaultServerItemMapper
        )(r)
      );
    });
  }

  private static DefaultServerItemMapper(item: any): DefaultItem {
    return {
      Id: item.Id,
      Name: item.Name,
    };
  }

  private getResultsFromServer(query: IQuery): angular.IPromise<Record[]> {
    query.Skip = 0;
    query.Take = 10;
    query.OrderBy = this.$scope.labelProp;

    const queryUrl = this.templateOptions.queryUrl;

    return this.templateOptions.useWrappedQuery
      ? this.serverUtility
          .queryWrapped<Record>({ url: queryUrl, query })
          .then((r) => r.Results)
      : this.serverUtility
          .query<Record>({ url: queryUrl, query })
          .then((r) => r.Results);
  }

  private static defaultQueryBuilder(search: string): IQuery {
    return {
      NameContains: search,
    };
  }
}
